import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, takeUntil } from 'rxjs';

import { APIError, HighlightsAPIService } from 'shared/services';
import { Highlight } from 'shared/models';
import { ToastService } from 'shared/ui';

import { USER_HIGHLIGHT_STYLES } from '../contents/contents.component';
import { AnnotationListComponent } from '../annotation-list/annotation-list.component';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { EventBusService } from '../../services/event-bus.service';
import { UIStateService } from '../../services/ui-state.service';

const USER_HIGHLIGHT_STYLE_MAP = USER_HIGHLIGHT_STYLES.reduce((map, style) => {
  map[style.styleClass] = style.style;
  return map;
}, {} as { [styleClass: string]: string });

@Component({
  selector: 'viewer-reading-group-records-underline',
  templateUrl: './reading-group-records-underline.component.html',
  styleUrls: [
    '../annotation-list/annotation-list.component.scss',
    './reading-group-records-underline.component.scss',
  ],
})
export class ReadingGroupRecordsUnderlineComponent
  extends AnnotationListComponent<Highlight>
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('underlineListContainer', { static: false })
  listContainer?: ElementRef;

  @Input() isMine = true;
  @Input() isOwnedByUser = false;
  @Input()
  set memberName(memberName: string | undefined) {
    if (this._memberName !== memberName) {
      this._memberName = memberName;
    }
  }
  @Input()
  set memberId(memberId: string | undefined) {
    if (this._memberId !== memberId) {
      this._memberId = memberId;
      if (this.bid) {
        this.loadItems();
      }
    }
  }

  private _memberId?: string;
  private _memberName?: string;
  private _scrollTimeout?: any; // 스크롤 타이머 추가

  get memberName(): string | undefined {
    return this._memberName;
  }

  get memberId(): string | undefined {
    return this._memberId;
  }

  private _unsubscriber = new Subject<void>();

  public readonly _styleClassStyleMap = USER_HIGHLIGHT_STYLE_MAP;

  public _selectedFilter: string | null = null;

  public __items: AnnotationListComponent<Highlight>['_items'] = [];

  private _bid?: string;

  public _isCommentaryEditorMode = false;
  public _isLoading = false;

  private _onScroll: (() => void) | null = null; // 스크롤 이벤트 핸들러 저장

  constructor(
    private _clipboard: Clipboard,
    private _highlightsAPIService: HighlightsAPIService,
    private _toastService: ToastService,
    private _annotationsService: AnnotationsV2Service,
    private _eventBusService: EventBusService,
    private _uiStateService: UIStateService
  ) {
    super();
  }

  override ngOnInit(): void {
    this._isCommentaryEditorMode = this._uiStateService.isCommentaryEditor;

    this._eventBusService
      .on('ContentsComponent:highlightUpdated')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this.loadItems();
      });

    // 내 기록 탭이 열릴 때마다 데이터 새로고침
    this._eventBusService
      .on('RightPanelComponent:myAnnotationsGroupOpened')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        if (this.isMine && this.bid) {
          this.loadItems();
        }
      });

    // 전체 댓글 탭 클릭 시 선택 취소
    this._eventBusService
      .on('RightPanelComponent:opened')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ activePanel }) => {
        if (activePanel === 'comment') {
          this._deselectAll();
        }
      });

    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    // 이벤트 리스너 설정을 별도 메서드로 분리
    this._setupScrollListener();
  }

  // 스크롤 리스너 설정 메서드 추가
  private _setupScrollListener(): void {
    // listContainer가 아직 없으면 다음 변경 감지 사이클에 다시 시도
    if (!this.listContainer?.nativeElement) {
      setTimeout(() => this._setupScrollListener(), 0);
      return;
    }

    const element = this.listContainer.nativeElement;

    // 기존 리스너가 있다면 제거 (중복 방지)
    element.removeEventListener('scroll', this._onScroll);
    element.removeEventListener('scroll', this._closeAllMenus);

    // 스크롤 이벤트 핸들러
    this._onScroll = (): void => {
      // 스크롤 중일 때 클래스 추가
      element.classList.add('is-scrolling');

      // 기존 타이머가 있으면 제거
      if (this._scrollTimeout) {
        clearTimeout(this._scrollTimeout);
      }

      // 스크롤이 멈춘 후 300ms 후에 클래스 제거
      this._scrollTimeout = setTimeout(() => {
        element.classList.remove('is-scrolling');
      }, 300);
    };

    element.addEventListener('scroll', this._onScroll);
    element.addEventListener('scroll', this._closeAllMenus);
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();

    // 타이머 정리
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }

    // 이벤트 리스너 제거
    if (this.listContainer?.nativeElement && this._onScroll) {
      this.listContainer.nativeElement.removeEventListener(
        'scroll',
        this._onScroll
      );
      this.listContainer.nativeElement.removeEventListener(
        'scroll',
        this._closeAllMenus
      );
    }
  }

  @Input()
  set bid(bid: string | undefined) {
    if (this._bid !== bid) {
      this._bid = bid;
      if (this._bid) {
        this.loadItems();
      }
    }
  }

  get bid(): string | undefined {
    return this._bid;
  }

  override loadItems(): void {
    if (!this.bid) {
      return;
    }

    // 이미 데이터가 있으면 로딩 상태를 표시하지 않음
    const hasExistingData = this.__items.length > 0;
    if (!hasExistingData) {
      this._isLoading = true;
    }

    this._highlightsAPIService
      .get(
        this.bid,
        0,
        999,
        this._isCommentaryEditorMode
          ? {
              commentary_id: this._annotationsService.getState().commentaryId,
              filter: 'theirs',
            }
          : {
              group_id: this._annotationsService.getState().groupId,
              filter: this.isMine ? 'mine' : 'theirs',
            },
        this._memberId
      )
      .subscribe((highlights) => {
        this.__items = highlights.map((highlight) => ({
          ...highlight,
          selected: false,
          updatedDateTime: new Date(highlight.created_at).getTime(),
        }));
        this._applyFilter();

        this._isLoading = false;

        // 데이터 로딩 완료 후 스크롤 리스너 설정
        setTimeout(() => {
          this._setupScrollListener();
        }, 0);
      });
  }

  _onShareUnderline(): void {
    const url = window.location.origin;
    const pathWithQuery = window.location.pathname + window.location.search;
    navigator.clipboard.writeText(url + pathWithQuery + '&isUnderline=true');

    this._toastService.open('밑줄 공유 링크가 복사되었습니다.');
  }

  _onCopySelectedItemsButtonClick(): void {
    this._clipboard.copy(
      this._getSelectedItems()
        .map((item) => item.text)
        .join('\n\n')
    );

    this._toastService.open('밑줄이 복사되었습니다.');
  }

  _onDeleteMenuClick(item: Highlight): void {
    this._deleteItems([item.id]);
  }

  _onDeleteSelectedItemsButtonClick(): void {
    const ids = this._getSelectedItems().map((item) => item.id);
    this._deleteItems(ids);
  }

  _onFilterChange(): void {
    this._applyFilter();
  }

  _applyFilter(): void {
    this._items = !this._selectedFilter
      ? this.__items
      : this.__items.filter((item) => item.style === this._selectedFilter);
    this._sortItems();
  }

  _onItemClick(item: Highlight): void {
    this.itemClicked.emit(item.url);
  }

  private _deleteItems(ids: string[]): void {
    if (
      this._uiStateService.isCommentaryEditor &&
      !this._uiStateService.canEditCommentary
    ) {
      this._toastService.openWarning(
        '판매 설정이 완료된 코멘터리는 수정이 불가합니다.'
      );

      return;
    }

    this._annotationsService.deleteHighlightsById(ids).subscribe({
      next: () => {
        this.__items = this.__items.filter(
          (item) => ids.indexOf(item.id) === -1
        );
        this._applyFilter();
        this._toastService.open('밑줄이 삭제되었습니다.');
      },
      error: (error: APIError) => {
        this._toastService.open(error.message);
      },
    });
  }

  // 모든 열린 메뉴를 닫는 메서드
  private _closeAllMenus(): void {
    const backdropClickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: 0,
      clientY: 0,
    });

    // document.body에 클릭 이벤트를 발생시켜 CDK의 backdrop 클릭 처리 로직을 트리거
    document.body.dispatchEvent(backdropClickEvent);

    // 또는 더 직접적으로 overlay backdrop을 찾아서 클릭 이벤트 발생
    const overlayBackdrop = document.querySelector('.cdk-overlay-backdrop');
    if (overlayBackdrop) {
      overlayBackdrop.dispatchEvent(backdropClickEvent);
    }
  }

  _onViewTextClick(item: Highlight): void {
    this._eventBusService.fire('AnnotationsComponent:itemClick', {
      url: item.url,
    });
  }
}
